import { useCallback } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import type { ClassesReportTableRow } from '@/features/classes-report/normalizeGroupReportRows';
import {
  BulkOperationResponseData,
  BulkOperationState,
} from '@/school-management/types/services/bulk';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import {
  getBulkOperationEntryId,
  getBulkOperationEntrySuccess,
} from '@/school-management/utils/bulkOperationEntry';
import processing from '@/school-management/utils/processing';
import moveStyles from '../MoveStudents/MoveStudentsPopup.module.scss';

interface DeleteClassesDownloadProps {
  selectedClasses: ClassesReportTableRow[];
  bulkOperation?: BulkOperationResponseData;
  bulkOperationState:
    | BulkOperationState.Completed
    | BulkOperationState.PartialSuccess
    | BulkOperationState.Failed;
}

const DeleteClassesDownload = ({
  selectedClasses,
  bulkOperation,
  bulkOperationState,
}: DeleteClassesDownloadProps) => {
  const { t } = useAdminTranslation();

  const download = useCallback(() => {
    const results = bulkOperation?.results || [];
    const resultById = results.reduce<Record<string, { success: boolean }>>((acc, r) => {
      const id = getBulkOperationEntryId(r);
      if (id) acc[id] = { success: getBulkOperationEntrySuccess(r) };
      return acc;
    }, {});

    const tableName = t('bulkActions.deleteClasses.report.fileName');
    const keys = {
      className: t('bulkActions.deleteClasses.report.className'),
      teacher: t('bulkActions.common.teacher'),
      grade: t('grade'),
      action: t('bulkActions.common.action'),
      status: t('status'),
      actionDelete: t('bulkActions.deleteClasses.report.actionDelete'),
      statusSuccess: t('bulkActions.deleteClasses.report.statusSuccess'),
      statusFailure: t('bulkActions.deleteClasses.report.statusFailure'),
    };

    const headerRow = [keys.className, keys.teacher, keys.grade, keys.action, keys.status];

    const data = selectedClasses.map(row => {
      const item = resultById[row.id];
      let success: boolean;
      if (bulkOperationState === BulkOperationState.Completed) {
        success = true;
      } else if (bulkOperationState === BulkOperationState.Failed) {
        success = item?.success ?? false;
      } else {
        success = item?.success ?? false;
      }

      return {
        [keys.className]: row.name,
        [keys.teacher]: row.teacher,
        [keys.grade]: row.grade,
        [keys.action]: keys.actionDelete,
        [keys.status]: success ? keys.statusSuccess : keys.statusFailure,
      };
    });

    processing.createXlsx({
      fileName: tableName,
      tables: [
        {
          name: tableName,
          headerRow,
          data,
        },
      ],
    });
  }, [bulkOperation, bulkOperationState, selectedClasses, t]);

  return (
    <div className={moveStyles.download}>
      <button type='button' onClick={download}>
        <AppIcon icon='download' width={10} height={15} />
        <span>{t('bulkActions.downloadChanges.downloadButton')}</span>
      </button>
      <div>{t('bulkActions.downloadChanges.text')}</div>
    </div>
  );
};

export default DeleteClassesDownload;
